// ─────────────────────────────────────────────────────────────────────
// <EventRail /> — horizontal scroll card rail (BMS mechanic)
//
// Shows 4 cards visible + peek of 5th on right edge.
// Arrow buttons advance by 1 card width.
// Smooth scroll-snap. Touch swipe native on mobile.
// ─────────────────────────────────────────────────────────────────────

import { useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import EventCard from './EventCard';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function EventRail({
  events = [],
  title,
  titleAccent,
  viewAllTo,
  className = '',
}) {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 8);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [events]);

  const scrollByCard = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector('[data-rail-card]');
    if (!card) return;
    const cardWidth = card.getBoundingClientRect().width + 18; // gap
    el.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
  };

  if (!events.length) return null;

  return (
    <section className={`relative ${className}`} aria-labelledby={title ? `rail-${title}` : undefined}>
      {(title || viewAllTo) && (
        <div className="flex items-baseline justify-between px-9 pt-14 pb-6">
          {title && (
            <h2
              id={`rail-${title}`}
              className="font-serif font-light"
              style={{
                fontSize: 30,
                lineHeight: 1.2,
                letterSpacing: '-0.015em',
                color: 'var(--snt-text)',
              }}
            >
              {title}
              {titleAccent && (
                <span className="snt-em-accent ml-2">{titleAccent}</span>
              )}
            </h2>
          )}
          {viewAllTo && (
            <Link
              to={viewAllTo}
              className="text-[12px] no-underline transition-colors"
              style={{ color: 'var(--snt-text-3)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--snt-accent)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--snt-text-3)')}
            >
              {t('common.view_all', 'View all')} →
            </Link>
          )}
        </div>
      )}

      <div className="relative">
        {/* Left arrow */}
        {showLeft && (
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label={t('rail.scroll_left', 'Scroll left')}
            className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full items-center justify-center transition-opacity"
            style={{
              background: 'rgba(10,10,11,0.85)',
              backdropFilter: 'blur(8px)',
              border: '1px solid var(--snt-line-strong)',
              color: 'var(--snt-text)',
            }}
          >
            <ChevronLeft size={18} />
          </button>
        )}

        {/* Right arrow */}
        {showRight && (
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label={t('rail.scroll_right', 'Scroll right')}
            className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full items-center justify-center transition-opacity"
            style={{
              background: 'rgba(10,10,11,0.85)',
              backdropFilter: 'blur(8px)',
              border: '1px solid var(--snt-line-strong)',
              color: 'var(--snt-text)',
            }}
          >
            <ChevronRight size={18} />
          </button>
        )}

        {/* Right edge fade (signals more content) */}
        {showRight && (
          <div
            className="hidden md:block absolute right-0 top-0 bottom-0 w-16 pointer-events-none z-0"
            style={{
              background: 'linear-gradient(to left, var(--snt-bg), transparent)',
            }}
          />
        )}

        {/* Scroll track */}
        <div
          ref={scrollRef}
          className="flex gap-[18px] overflow-x-auto scroll-smooth snap-x snap-mandatory snt-no-scrollbar px-9 pb-12"
          style={{ scrollbarWidth: 'none' }}
        >
          {events.map((event) => (
            <div
              key={event.id || event.slug}
              data-rail-card
              className="flex-shrink-0 snap-start"
              style={{
                // Card widths: 4 visible + peek on desktop, 1.7 on mobile
                width: 'clamp(220px, calc((100vw - 220px) / 4.3), 290px)',
              }}
            >
              <EventCard event={event} />
            </div>
          ))}
        </div>
        <style>{`.snt-no-scrollbar::-webkit-scrollbar{display:none;}`}</style>
      </div>
    </section>
  );
}
